import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatErr } from "@/lib/api";
import {
  X, CheckCircle2, AlertTriangle, Clock, Sparkles, Flame, ShieldCheck,
  CheckCheck, Wrench, ThumbsUp, ThumbsDown, RotateCcw, Camera, ZoomIn, FileX,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Build an authenticated <img> src for a stored evidence photo. The
// /branding/file endpoint accepts an `auth` query param so the browser can
// load images that aren't otherwise publicly registered.
function evidenceSrc(url) {
  if (!url) return "";
  const base = /^https?:\/\//.test(url) ? url : `${BACKEND_URL}${url}`;
  const token = localStorage.getItem("vb_access_token");
  return token ? `${base}?auth=${encodeURIComponent(token)}` : base;
}

// Upload one image file → returns the stored URL.
async function uploadEvidence(file, kind) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(`/checks/evidence?kind=${kind}`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.url;
}

// --------------------------- Photo Lightbox ---------------------------
function Lightbox({ src, onClose }) {
  return createPortal(
    <div className="vb-modal-bg z-[2000]" onClick={onClose} data-testid="photo-lightbox">
      <img
        src={src} alt="evidence"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "92vw", maxHeight: "90vh", borderRadius: 10, boxShadow: "0 8px 40px rgba(0,0,0,0.5)" }}
      />
    </div>,
    document.body,
  );
}

// --------------------------- Photo Thumbnails (read-only) ---------------------------
export function PhotoThumbs({ photos, label, testidPrefix = "photo" }) {
  const [zoom, setZoom] = useState(null);
  if (!photos || photos.length === 0) return null;
  return (
    <div className="mt-1.5">
      {label && <div className="text-xs uppercase tracking-wider opacity-60 mb-1">{label}</div>}
      <div className="flex gap-2 flex-wrap">
        {photos.map((u, i) => (
          <button
            key={i} type="button"
            className="relative rounded-md overflow-hidden border group"
            style={{ width: 56, height: 56, borderColor: "rgba(0,0,0,0.12)", padding: 0, cursor: "zoom-in" }}
            onClick={() => setZoom(evidenceSrc(u))}
            data-testid={`${testidPrefix}-thumb-${i}`}
            title="View full photo"
          >
            <img src={evidenceSrc(u)} alt={`evidence ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100"
              style={{ background: "rgba(0,0,0,0.35)", color: "#fff", transition: "opacity 120ms" }}>
              <ZoomIn size={16} />
            </span>
          </button>
        ))}
      </div>
      {zoom && <Lightbox src={zoom} onClose={() => setZoom(null)} />}
    </div>
  );
}

// --------------------------- Photo Evidence Field (upload) ---------------------------
export function PhotoEvidenceField({ photos = [], onChange, kind, label = "Photo evidence", testid = "photo-evidence", onUploadingChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(null);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true); onUploadingChange?.(true);
    try {
      const urls = [];
      for (const f of files) {
        if (!/^image\//.test(f.type)) continue;
        if (f.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8MB)"); continue; }
        urls.push(await uploadEvidence(f, kind));
      }
      if (urls.length) onChange([...(photos || []), ...urls]);
    } catch (ex) {
      toast.error(formatErr(ex.response?.data?.detail) || "Photo upload failed");
    } finally { setBusy(false); onUploadingChange?.(false); }
  };
  const remove = (i) => onChange((photos || []).filter((_, idx) => idx !== i));

  return (
    <div className="mt-2" data-testid={testid}>
      <div className="flex items-center gap-2 flex-wrap">
        <input
          ref={inputRef} type="file" accept="image/*" capture="environment"
          multiple className="hidden" onChange={onFiles}
          data-testid={`${testid}-input`}
        />
        <button
          type="button" className="vb-btn ghost text-xs"
          onClick={() => inputRef.current?.click()} disabled={busy}
          data-testid={`${testid}-btn`}
        >
          <Camera size={13} /> {busy ? "Uploading…" : label}
        </button>
        {(photos || []).length > 0 && (
          <span className="text-xs opacity-60">{photos.length} photo{photos.length === 1 ? "" : "s"} attached</span>
        )}
      </div>
      {(photos || []).length > 0 && (
        <div className="flex gap-2 flex-wrap mt-2">
          {photos.map((u, i) => (
            <div key={i} className="relative rounded-md overflow-hidden border" style={{ width: 56, height: 56, borderColor: "rgba(0,0,0,0.12)" }}>
              <img
                src={evidenceSrc(u)} alt={`evidence ${i + 1}`}
                style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "zoom-in" }}
                onClick={() => setZoom(evidenceSrc(u))}
                data-testid={`${testid}-preview-${i}`}
              />
              <button
                type="button"
                className="absolute top-0 right-0 flex items-center justify-center"
                style={{ width: 18, height: 18, background: "rgba(0,0,0,0.6)", color: "#fff", border: "none", cursor: "pointer", borderBottomLeftRadius: 6 }}
                onClick={() => remove(i)}
                data-testid={`${testid}-remove-${i}`}
                title="Remove photo"
              >
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
      {zoom && <Lightbox src={zoom} onClose={() => setZoom(null)} />}
    </div>
  );
}

// --------------------------- Rework History (read-only) ---------------------------
function ReworkOutcomePill({ outcome }) {
  const map = {
    pending: { cls: "warn", label: "Pending review" },
    approved: { cls: "ok", label: "Approved" },
    rejected: { cls: "err", label: "Rejected" },
  };
  const m = map[outcome] || map.pending;
  return <span className={`vb-pill ${m.cls} text-[11px]`}>{m.label}</span>;
}

export function ReworkHistory({ history, testidPrefix = "rework" }) {
  if (!history || history.length === 0) return null;
  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-history`}>
      {history.map((h, i) => {
        const num = h.attempt || i + 1;
        return (
          <div
            key={i} className="vb-card p-2.5"
            style={{ background: "var(--vb-bg-2)" }}
            data-testid={`${testidPrefix}-attempt-${num}`}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs uppercase tracking-wider opacity-60">Attempt {num}</span>
              <ReworkOutcomePill outcome={h.outcome} />
              {h.at && <span className="text-xs opacity-50 ml-auto">{new Date(h.at).toLocaleString()}</span>}
            </div>
            <div className="text-base mt-1">{h.notes || "—"}</div>
            {h.by_name && <div className="text-xs opacity-60 mt-0.5">by {h.by_name}</div>}
            {(h.photos || []).length > 0 && (
              <PhotoThumbs photos={h.photos} testidPrefix={`${testidPrefix}-${num}-photo`} />
            )}
            {h.outcome === "rejected" && h.decision_notes && (
              <div className="text-xs mt-1" style={{ color: "var(--vb-error)" }}>
                <strong>Rejected:</strong> {h.decision_notes}
                {h.decided_by_name ? ` · ${h.decided_by_name}` : ""}
              </div>
            )}
            {h.outcome === "approved" && h.decision_notes && (
              <div className="text-xs mt-1 opacity-80">
                <strong>Approved:</strong> {h.decision_notes}
                {h.decided_by_name ? ` · ${h.decided_by_name}` : ""}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// --------------------------- Streak Meter ---------------------------
export function StreakMeter({ streak, label = "Hygiene streak", subtitle }) {
  const s = streak.summary;
  return (
    <div className="vb-card p-5" data-testid="streak-meter">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="vb-heading text-base m-0 mb-1" style={{ color: "var(--vb-text-2)" }}>
            <Flame size={16} className="inline-block mr-1.5 -mt-0.5" style={{ color: s.current_streak > 0 ? "var(--vb-accent)" : undefined }} />
            {label}
          </h3>
          <p className="text-xs opacity-60 m-0">{subtitle || `Last ${streak.days} days · daily personnel hygiene`}</p>
        </div>
        <div className="flex gap-4 text-right text-xs">
          <Stat label="Current" value={`${s.current_streak}d`} accent={s.current_streak > 0} testid="streak-current" />
          <Stat label="Longest" value={`${s.longest_streak}d`} testid="streak-longest" />
          <Stat label="Compliance" value={`${s.compliance_pct}%`} testid="streak-pct" />
        </div>
      </div>

      <div className="flex gap-1 mb-2 flex-wrap" data-testid="streak-strip">
        {streak.series.map((d) => (
          <span
            key={d.date}
            className={`vb-streak-dot vb-streak-${d.status}`}
            title={`${d.date} · ${d.status === "none" ? "no check" : d.status}`}
            data-testid={`streak-dot-${d.date}`}
          />
        ))}
      </div>
      <div className="flex gap-3 text-[11px] opacity-70">
        <span><span className="vb-streak-dot vb-streak-pass mr-1" /> All-pass day</span>
        <span><span className="vb-streak-dot vb-streak-fail mr-1" /> Failure logged</span>
        <span><span className="vb-streak-dot vb-streak-none mr-1" /> No check</span>
      </div>
      {s.current_streak >= 7 && (
        <div className="mt-3 vb-pill ok text-xs inline-flex items-center" data-testid="streak-celebrate">
          <Sparkles size={12} /> Nice — {s.current_streak} clean days in a row!
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-60">{label}</div>
      <div className="vb-heading text-lg leading-none"
        style={{ color: accent ? "var(--vb-accent)" : "var(--vb-text-2)" }}>
        {value}
      </div>
    </div>
  );
}

// --------------------------- per-item state pill ---------------------------
const ITEM_STATE_META = {
  awaiting_verify:        { pill: "warn", label: "Awaiting verify" },
  awaiting_rectification: { pill: "err",  label: "Awaiting rectification" },
  awaiting_approval:      { pill: "warn", label: "Awaiting approval" },
  rejected:               { pill: "err",  label: "Rectification rejected" },
  rectified:              { pill: "ok",   label: "Rectified" },
};

function ItemStatePill({ state }) {
  const meta = ITEM_STATE_META[state];
  if (!meta) return null;
  return <span className={`vb-pill ${meta.pill} text-[10px]`} data-testid={`item-state-${state}`}>{meta.label}</span>;
}

// --------------------------- Check Row (read-only + actions) ---------------------------
export function CheckRow({ check: c, template, canVerify, onVerified }) {
  const { user: me } = useAuth();
  const [open, setOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [rectifyItem, setRectifyItem] = useState(null);
  const [approveItem, setApproveItem] = useState(null);
  const [busy, setBusy] = useState(false);

  const itemLabel = (key) => {
    for (const sec of template?.sections || []) {
      const it = (sec.items || []).find((i) => i.key === key);
      if (it) return it.label;
    }
    return key;
  };

  // Simple all-pass verify (no corrective actions needed).
  const verifyAllPass = async () => {
    setBusy(true);
    try { await api.post(`/checks/${c.id}/verify`, { notes: "" }); onVerified?.(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Verify failed"); }
    finally { setBusy(false); }
  };

  const onVerifyClick = () => {
    if ((c.fail_count || 0) > 0) setVerifyOpen(true);
    else verifyAllPass();
  };

  const failed = (c.items || []).filter((i) => i.status === "fail");
  const dt = new Date(c.checked_at);
  // Whether THIS user can rectify a failed item on this check.
  const canRectify = !!me && ["owner", "manager", "supervisor", "staff"].includes(me?.role);
  const phase = c.rectification_phase;

  // Items the current user can act on right now — surfaced as prominent
  // header buttons (beside "Show details") so the next step is obvious.
  const rectifiableItems = (c.items || []).filter(
    (i) => i.status === "fail" && (i.state === "awaiting_rectification" || i.state === "rejected"),
  );
  const approvableItems = (c.items || []).filter(
    (i) => i.status === "fail" && i.state === "awaiting_approval",
  );
  const openRectify = () => {
    if (rectifiableItems.length === 1) setRectifyItem(rectifiableItems[0]);
    else setOpen(true);
  };
  const openApprove = () => {
    if (approvableItems.length === 1) setApproveItem(approvableItems[0]);
    else setOpen(true);
  };

  return (
    <div className="vb-card p-3 border" style={{ background: "var(--vb-bg)" }} data-testid={`check-row-${c.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            {c.overall === "fail"
              ? <span className="vb-pill err text-[10px]" data-testid="check-overall-fail"><AlertTriangle size={10} /> {c.fail_count} fail{c.fail_count === 1 ? "" : "s"}</span>
              : <span className="vb-pill ok text-[10px]" data-testid="check-overall-pass"><CheckCircle2 size={10} /> All pass</span>}
            {c.verified_at
              ? <span className="vb-pill ok text-[10px]" title={`Verified by ${c.verified_by_name || "—"} at ${new Date(c.verified_at).toLocaleString()}`} data-testid="check-verified"><ShieldCheck size={10} /> Verified</span>
              : <span className="vb-pill warn text-[10px]" data-testid="check-unverified"><Clock size={10} /> Unverified</span>}
            {phase === "awaiting_rectification" && (
              <span className="vb-pill err text-[10px]" data-testid="phase-awaiting-rectification">
                <Wrench size={10} /> {c.pending_rectification_count} to rectify
              </span>
            )}
            {phase === "awaiting_approval" && (
              <span className="vb-pill warn text-[10px]" data-testid="phase-awaiting-approval">
                <Clock size={10} /> {c.pending_approval_count} to approve
              </span>
            )}
            {phase === "closed" && (
              <span className="vb-pill ok text-[10px]" data-testid="phase-closed">
                <CheckCheck size={10} /> Closed
              </span>
            )}
            <span className="opacity-60 text-[11px]">{dt.toLocaleDateString()} {dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
          </div>
          <div className="text-xs opacity-70">
            {c.scope_target_name ? <>For <strong className="opacity-90">{c.scope_target_name}</strong> · </> : null}
            Logged by <strong className="opacity-90">{c.checked_by_name || "—"}</strong>
            {c.remarks && <span className="ml-2">· {c.remarks}</span>}
          </div>
          {failed.length > 0 && (
            <ul className="text-xs mt-1 mb-0 list-disc pl-5" style={{ color: "var(--vb-error)" }}>
              {failed.map((f) => (
                <li key={f.item_key}>
                  <strong>{f.label || itemLabel(f.item_key)}</strong>
                  {f.notes ? ` — ${f.notes}` : ""}
                  {f.state && f.state !== "awaiting_verify" && (
                    <span className="ml-2"><ItemStatePill state={f.state} /></span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex gap-1 flex-wrap justify-end">
          <button className="vb-btn ghost text-xs" onClick={() => setOpen((v) => !v)} data-testid={`toggle-check-${c.id}`}>
            {open ? "Hide details" : "Show details"}
          </button>
          {canRectify && rectifiableItems.length > 0 && (
            <button
              className="vb-btn text-xs"
              onClick={openRectify}
              title="Submit your rectification for the corrective action"
              data-testid={`rectify-check-${c.id}`}
            >
              <Wrench size={13} /> Rectified Task{rectifiableItems.length > 1 ? ` (${rectifiableItems.length})` : ""}
            </button>
          )}
          {canVerify && approvableItems.length > 0 && (
            <button
              className="vb-btn text-xs"
              style={{ background: "var(--vb-accent)", borderColor: "var(--vb-accent)", color: "#fff" }}
              onClick={openApprove}
              title="Review the staff rectification and close the item"
              data-testid={`review-rect-check-${c.id}`}
            >
              <ThumbsUp size={13} /> Review Rectified Task{approvableItems.length > 1 ? ` (${approvableItems.length})` : ""}
            </button>
          )}
          {canVerify && !c.verified_at && (
            (c.fail_count || 0) > 0 ? (
              <button
                className="vb-btn"
                style={{ background: "var(--vb-error)", borderColor: "var(--vb-error)", color: "#fff" }}
                onClick={onVerifyClick} disabled={busy}
                title="Verify & assign corrective actions for failed items"
                data-testid={`verify-check-${c.id}`}
              >
                <AlertTriangle size={13} /> Verify & assign actions
              </button>
            ) : (
              <button className="vb-btn" onClick={onVerifyClick} disabled={busy} title="Verify" data-testid={`verify-check-${c.id}`}>
                <ShieldCheck size={13} /> Verify
              </button>
            )
          )}
        </div>
      </div>

      {open && (
        <div className="mt-3 space-y-2" data-testid={`check-details-${c.id}`}>
          {(c.items || []).map((it) => {
            const isFail = it.status === "fail";
            const state = it.state || (isFail ? "awaiting_verify" : "ok");
            return (
              <div key={it.item_key} className="text-xs border-t pt-2" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                <div className="flex items-start gap-2">
                  <StatusDot status={it.status} />
                  <span className="flex-1">
                    <strong>{it.label || itemLabel(it.item_key)}</strong>
                    {it.notes ? <span className="opacity-70"> — {it.notes}</span> : null}
                  </span>
                  {isFail && <ItemStatePill state={state} />}
                </div>
                {isFail && (it.photos || []).length > 0 && (
                  <div className="pl-5">
                    <PhotoThumbs photos={it.photos} label="Failure evidence" testidPrefix={`fail-photo-${c.id}-${it.item_key}`} />
                  </div>
                )}
                {isFail && it.corrective_action && (
                  <div className="mt-1 pl-5 text-[11px] opacity-90">
                    <strong>Corrective action:</strong> {it.corrective_action}
                    {it.corrective_action_set_by_name && (
                      <span className="opacity-60"> · by {it.corrective_action_set_by_name}</span>
                    )}
                  </div>
                )}
                {isFail && (it.rectification_history?.length ? (
                  <div className="mt-1 pl-5">
                    <ReworkHistory history={it.rectification_history} testidPrefix={`rework-${c.id}-${it.item_key}`} />
                  </div>
                ) : (
                  <>
                    {it.rectification_notes && (
                      <div className="mt-1 pl-5 text-[11px] opacity-90">
                        <strong>Staff rectified:</strong> {it.rectification_notes}
                        {it.rectified_by_name && (
                          <span className="opacity-60"> · {it.rectified_by_name} · {new Date(it.rectified_at).toLocaleString()}</span>
                        )}
                      </div>
                    )}
                    {(it.rectification_photos || []).length > 0 && (
                      <div className="pl-5">
                        <PhotoThumbs photos={it.rectification_photos} label="Rectification evidence" testidPrefix={`rect-photo-${c.id}-${it.item_key}`} />
                      </div>
                    )}
                  </>
                ))}
                {isFail && state === "rejected" && it.rectification_rejection_notes && (
                  <div className="mt-1 pl-5 text-[11px]" style={{ color: "var(--vb-error)" }}>
                    <strong>Rejected:</strong> {it.rectification_rejection_notes}
                  </div>
                )}
                {isFail && state === "rectified" && it.rectification_approval_notes && (
                  <div className="mt-1 pl-5 text-[11px] opacity-80">
                    <strong>Approved:</strong> {it.rectification_approval_notes}
                    {it.rectification_approved_by_name && (
                      <span className="opacity-60"> · {it.rectification_approved_by_name}</span>
                    )}
                  </div>
                )}
                {/* Per-item actions */}
                {isFail && (state === "awaiting_rectification" || state === "rejected") && canRectify && (
                  <div className="pl-5 mt-1.5">
                    <button
                      className="vb-btn text-xs"
                      onClick={() => setRectifyItem(it)}
                      data-testid={`rectify-item-${c.id}-${it.item_key}`}
                    >
                      <Wrench size={11} /> Rectified Task
                    </button>
                  </div>
                )}
                {isFail && state === "awaiting_approval" && canVerify && (
                  <div className="pl-5 mt-1.5">
                    <button
                      className="vb-btn text-xs"
                      onClick={() => setApproveItem(it)}
                      data-testid={`approve-item-${c.id}-${it.item_key}`}
                    >
                      <ThumbsUp size={11} /> Review rectification
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {verifyOpen && (
        <VerifyWithCorrectiveActionModal
          check={c} template={template}
          onClose={() => setVerifyOpen(false)}
          onSaved={() => { setVerifyOpen(false); onVerified?.(); }}
        />
      )}
      {rectifyItem && (
        <RectifyItemModal
          check={c} item={rectifyItem} template={template}
          onClose={() => setRectifyItem(null)}
          onSaved={() => { setRectifyItem(null); onVerified?.(); }}
        />
      )}
      {approveItem && (
        <ApproveRectificationModal
          check={c} item={approveItem} template={template}
          onClose={() => setApproveItem(null)}
          onSaved={() => { setApproveItem(null); onVerified?.(); }}
        />
      )}
    </div>
  );
}

function StatusDot({ status }) {
  const map = {
    pass: { color: "var(--vb-success, #4F8A3A)", icon: <CheckCircle2 size={12} /> },
    fail: { color: "var(--vb-error)", icon: <X size={12} /> },
    na:   { color: "rgba(0,0,0,0.4)", icon: <span style={{ fontSize: 9, fontWeight: 700 }}>NA</span> },
  };
  const m = map[status] || map.pass;
  return <span style={{ color: m.color, display: "inline-flex", alignItems: "center" }}>{m.icon}</span>;
}

// --------------------------- Log Modal ---------------------------
export function LogCheckModal({ template, targetId, facility, onClose, onSaved }) {
  const all = useMemo(() => {
    const items = [];
    for (const sec of template.sections || []) {
      for (const it of sec.items || []) items.push(it);
    }
    return items;
  }, [template]);

  const [statuses, setStatuses] = useState(() => Object.fromEntries(all.map((i) => [i.key, "pass"])));
  const [notesByKey, setNotesByKey] = useState({});
  const [photosByKey, setPhotosByKey] = useState({});
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [err, setErr] = useState("");

  const setStatus = (k, v) => setStatuses((s) => ({ ...s, [k]: v }));
  const setNote = (k, v) => setNotesByKey((s) => ({ ...s, [k]: v }));
  const setPhotos = (k, v) => setPhotosByKey((s) => ({ ...s, [k]: v }));
  const onUploading = (b) => setUploadingCount((n) => Math.max(0, n + (b ? 1 : -1)));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const items = all.map((i) => ({
        item_key: i.key,
        status: statuses[i.key] || "pass",
        notes: (notesByKey[i.key] || "").trim(),
        photos: statuses[i.key] === "fail" ? (photosByKey[i.key] || []) : [],
      }));
      await api.post("/checks", {
        template_code: template.code,
        scope_target_id: targetId,
        items,
        remarks: remarks.trim(),
      });
      onSaved();
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const failCount = all.filter((i) => statuses[i.key] === "fail").length;

  return createPortal(
    <div className="vb-modal-bg" onClick={onClose} data-testid="log-check-backdrop">
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }} data-testid="log-check-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="vb-heading text-xl m-0">{template.title}</h2>
            {facility ? (
              <p className="text-sm opacity-70 m-0" data-testid="log-check-subtitle">
                <strong style={{ color: "var(--vb-text-2)" }}>{facility.site_name || "—"}</strong>
                {facility.name ? <> · {facility.name}</> : null}
                {facility.person_in_charge ? <span className="opacity-70"> · PIC: {facility.person_in_charge}</span> : null}
              </p>
            ) : (
              <p className="text-xs opacity-60 m-0">{template.form_ref} · today's check</p>
            )}
          </div>
          <button className="vb-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>
        {err && <div className="vb-pill err mb-3" data-testid="log-check-error">{err}</div>}

        <form onSubmit={submit} className="space-y-3" data-testid="log-check-form">
          {(template.sections || []).map((sec) => (
            <div key={sec.name}>
              {(template.sections || []).length > 1 && (
                <h3 className="text-xs uppercase tracking-wider opacity-60 mb-2 mt-2">{sec.name}</h3>
              )}
              <div className="space-y-2">
                {(sec.items || []).map((it) => (
                  <ItemRow
                    key={it.key} item={it}
                    status={statuses[it.key]} notes={notesByKey[it.key] || ""}
                    photos={photosByKey[it.key] || []}
                    onStatus={(s) => setStatus(it.key, s)}
                    onNotes={(n) => setNote(it.key, n)}
                    onPhotos={(p) => setPhotos(it.key, p)}
                    onUploadingChange={onUploading}
                  />
                ))}
              </div>
            </div>
          ))}

          <div>
            <label className="text-xs opacity-70">General remarks</label>
            <textarea
              className="vb-input" rows={2} maxLength={500}
              value={remarks} onChange={(e) => setRemarks(e.target.value)}
              placeholder="Optional — e.g. retraining recommended, follow-up tomorrow"
              data-testid="log-check-remarks"
            />
          </div>

          <div className="flex justify-between items-center pt-2 gap-3 flex-wrap">
            <span className={`text-xs ${failCount > 0 ? "" : "opacity-70"}`} style={failCount > 0 ? { color: "var(--vb-error)" } : undefined} data-testid="log-check-failsummary">
              {failCount > 0
                ? `${failCount} item${failCount === 1 ? "" : "s"} marked failed — please add notes if helpful`
                : "All items marked pass"}
            </span>
            <div className="flex gap-2">
              <button type="button" className="vb-btn ghost" onClick={onClose} data-testid="log-check-cancel">Cancel</button>
              <button type="submit" className="vb-btn" disabled={busy || uploadingCount > 0} data-testid="log-check-submit">
                {uploadingCount > 0 ? "Uploading photo…" : busy ? "Saving…" : "Submit check"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function ItemRow({ item, status, notes, photos, onStatus, onNotes, onPhotos, onUploadingChange }) {
  return (
    <div className="vb-check-item" data-testid={`item-${item.key}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-medium">{item.label}</div>
          {item.label_zh && <div className="text-[11px] opacity-50">{item.label_zh}</div>}
        </div>
        <div className="vb-status-pills" role="radiogroup">
          {["pass", "fail", "na"].map((s) => (
            <button
              key={s} type="button" role="radio" aria-checked={status === s}
              className={`vb-status-pill vb-status-${s} ${status === s ? "active" : ""}`}
              onClick={() => onStatus(s)}
              data-testid={`item-${item.key}-${s}`}
            >
              {s === "pass" ? "Pass" : s === "fail" ? "Fail" : "N/A"}
            </button>
          ))}
        </div>
      </div>
      {status === "fail" && (
        <>
          <input
            className="vb-input mt-2 text-xs"
            placeholder="Note for the failure (e.g. nail varnish observed)"
            value={notes} onChange={(e) => onNotes(e.target.value)}
            maxLength={200}
            data-testid={`item-${item.key}-notes`}
          />
          <PhotoEvidenceField
            photos={photos} onChange={onPhotos} kind="failure"
            label="Add photo of failure"
            testid={`item-${item.key}-photos`}
            onUploadingChange={onUploadingChange}
          />
        </>
      )}
    </div>
  );
}


// --------------------------- Pending Sign-off Panel (shared) ---------------------------
/**
 * Reusable bulk-verify panel for hygiene/cleanliness pending lists.
 *  - `pending`     : list of staff_checks rows
 *  - `template`    : matching template for label resolution inside CheckRow
 *  - `title`       : section heading (default: "Pending Action")
 *  - `onChanged()` : called after any verification — caller reloads
 */
export function PendingSignOffPanel({ pending, template, onChanged, title = "Pending Action",
                                     month, onMonthChange, monthHasData = true }) {
  const [busyBulk, setBusyBulk] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  // Last 12 month options for the supervisor backlog picker. Only
  // rendered when the parent passes `onMonthChange` (i.e. the caller
  // explicitly wants the historical-month UI — staff pages skip it).
  const monthOptions = useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleString(undefined, { month: "long", year: "numeric" });
      out.push({ value, label: i === 0 ? `${label} (current)` : label });
    }
    return out;
  }, []);

  // Split into "all-pass" (bulk-verifiable) and "with-fails" (need individual
  // verify + corrective action assignment).
  const passRows = useMemo(() => pending.filter((c) => (c.fail_count || 0) === 0), [pending]);
  const failRows = useMemo(() => pending.filter((c) => (c.fail_count || 0) > 0), [pending]);
  const passIds = useMemo(() => passRows.map((c) => c.id), [passRows]);
  const allSelected = passIds.length > 0 && passIds.every((id) => selected.has(id));
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(passIds));

  const verifySelected = async () => {
    if (selected.size === 0) return;
    setBusyBulk(true);
    try {
      await api.post("/checks/verify-bulk", { check_ids: Array.from(selected), notes: "" });
      setSelected(new Set());
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Bulk verify failed");
    } finally { setBusyBulk(false); }
  };

  return (
    <section data-testid="pending-section">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <ShieldCheck size={18} style={{ color: "var(--vb-text-2)" }} />
        <h2 className="vb-heading text-lg m-0" style={{ color: "var(--vb-text-2)" }}>{title}</h2>
        {pending.length > 0 && (
          <span className="vb-pill warn text-[10px]" data-testid="pending-count">{pending.length}</span>
        )}
        {/* Historical-month picker — manager+ only (parent controls
            visibility by passing onMonthChange). Lets a supervisor pull
            up back-dated checks uploaded via Excel catch-up. */}
        {onMonthChange && (
          <div className="ml-auto flex items-center gap-2 flex-wrap"
            data-testid="pending-month-picker">
            <label className="text-[11px] uppercase tracking-wider opacity-60">
              Backlog window
            </label>
            <select
              className="vb-input"
              style={{ width: "auto", minWidth: 180, padding: "6px 10px", fontSize: 13 }}
              value={month || ""}
              onChange={(e) => onMonthChange(e.target.value)}
              data-testid="pending-month-select"
            >
              <option value="">Last 30 days (default)</option>
              {monthOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {month && (
              <button
                type="button"
                className="vb-btn ghost text-[11px]"
                onClick={() => onMonthChange("")}
                data-testid="pending-month-reset"
                title="Switch back to the rolling 30-day window"
              >
                Reset
              </button>
            )}
          </div>
        )}
      </div>
      <div className="vb-card p-4">
        {pending.length === 0 ? (
          month && monthHasData === false ? (
            <div className="text-center py-6 text-sm" data-testid="pending-no-data">
              <FileX size={28} className="mx-auto mb-2" style={{ color: "var(--vb-warn, #b45309)" }} />
              <span className="font-bold text-base" style={{ color: "var(--vb-warn, #b45309)" }}>
                No data for {monthOptions.find((o) => o.value === month)?.label || month}
              </span>
              <p className="opacity-60 text-xs mt-1">
                Nothing has been logged for this month yet — switch months or log/upload checks.
              </p>
            </div>
          ) : (
            <div className="text-center py-6 opacity-60 text-sm" data-testid="pending-empty">
              <CheckCheck size={28} className="mx-auto opacity-40 mb-2" />
              {month
                ? <>No outstanding sign-offs in <strong>{monthOptions.find((o) => o.value === month)?.label || month}</strong>.</>
                : "All clear — no checks waiting for your sign-off."}
            </div>
          )
        ) : (
          <div className="space-y-4">
            {/* Fail rows — must verify individually so the corrective action can be assigned per item */}
            {failRows.length > 0 && (
              <div data-testid="pending-fail-group">
                <div className="flex items-start gap-2 mb-2 p-2 rounded-md"
                  style={{ background: "var(--vb-bg-2)" }}>
                  <AlertTriangle size={14} style={{ color: "var(--vb-error)" }} className="mt-0.5 shrink-0" />
                  <p className="text-xs m-0 leading-snug">
                    <strong>{failRows.length} record{failRows.length === 1 ? "" : "s"} with failed items.</strong>{" "}
                    Click <span className="font-semibold">"Verify & assign actions"</span> on each row to set a corrective action per failed item before sign-off. These cannot be bulk-verified.
                  </p>
                </div>
                <div className="space-y-2">
                  {failRows.map((c) => (
                    <div key={c.id} data-testid={`pending-row-${c.id}`}>
                      <CheckRow check={c} template={template} canVerify onVerified={onChanged} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pass rows — bulk-verifiable */}
            {passRows.length > 0 && (
              <div data-testid="pending-pass-group">
                {failRows.length > 0 && (
                  <div className="text-xs uppercase tracking-wider opacity-60 mb-2">
                    All-pass records · bulk sign-off
                  </div>
                )}
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <label className="text-xs flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox" checked={allSelected} onChange={toggleAll}
                      data-testid="bulk-select-all"
                    />
                    Select all ({passRows.length})
                  </label>
                  <button
                    className="vb-btn"
                    disabled={selected.size === 0 || busyBulk}
                    onClick={verifySelected}
                    data-testid="bulk-verify-btn"
                  >
                    <ShieldCheck size={13} /> {busyBulk ? "Verifying…" : `Verify selected (${selected.size})`}
                  </button>
                </div>
                <div className="space-y-2">
                  {passRows.map((c) => (
                    <div key={c.id} className="flex items-start gap-2" data-testid={`pending-row-${c.id}`}>
                      <input
                        type="checkbox" className="mt-3"
                        checked={selected.has(c.id)} onChange={() => toggle(c.id)}
                        data-testid={`pending-select-${c.id}`}
                      />
                      <div className="flex-1">
                        <CheckRow check={c} template={template} canVerify onVerified={onChanged} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

// --------------------------- Verify w/ Corrective Action Modal ---------------------------
/**
 * Opens when a supervisor clicks Verify on a check that has ANY failed items.
 * Requires a corrective action per failed item before submission.
 */
function VerifyWithCorrectiveActionModal({ check, template, onClose, onSaved }) {
  const failed = useMemo(
    () => (check.items || []).filter((i) => i.status === "fail"),
    [check],
  );
  const itemLabel = (key) => {
    for (const sec of template?.sections || []) {
      const it = (sec.items || []).find((i) => i.key === key);
      if (it) return it.label;
    }
    return key;
  };
  const [actions, setActions] = useState(() =>
    Object.fromEntries(failed.map((f) => [f.item_key, ""])),
  );
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const allFilled = failed.every((f) => (actions[f.item_key] || "").trim().length > 0);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!allFilled) {
      setErr("Please add a corrective action for every failed item.");
      return;
    }
    setBusy(true); setErr("");
    try {
      await api.post(`/checks/${check.id}/verify`, {
        notes: notes.trim(),
        corrective_actions: failed.map((f) => ({
          item_key: f.item_key,
          corrective_action: actions[f.item_key].trim(),
        })),
      });
      onSaved?.();
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Verify failed");
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="vb-modal-bg z-[1000]" onClick={onClose} data-testid="verify-ca-backdrop">
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }} data-testid="verify-ca-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="vb-heading text-xl m-0">Verify & assign corrective actions</h2>
            <p className="text-xs opacity-60 m-0">
              {check.scope_target_name ? <>{check.scope_target_name} · </> : null}
              {failed.length} failed item{failed.length === 1 ? "" : "s"} need a corrective action before sign-off.
            </p>
          </div>
          <button className="vb-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>
        {err && <div className="vb-pill err mb-3" data-testid="verify-ca-error">{err}</div>}
        <form onSubmit={submit} className="space-y-3" data-testid="verify-ca-form">
          {failed.map((f) => (
            <div key={f.item_key} className="vb-card p-3" style={{ background: "var(--vb-bg-2)" }}>
              <div className="text-sm font-medium" style={{ color: "var(--vb-text-2)" }}>
                <AlertTriangle size={12} className="inline -mt-0.5 mr-1" style={{ color: "var(--vb-error)" }} />
                {f.label || itemLabel(f.item_key)}
              </div>
              {f.notes && (
                <div className="text-[11px] opacity-70 mt-0.5">Staff note: {f.notes}</div>
              )}
              <label className="text-xs opacity-70 block mt-2 mb-1">Corrective action for staff (required)</label>
              <textarea
                className="vb-input" rows={2} maxLength={500}
                value={actions[f.item_key] || ""}
                onChange={(e) => setActions((s) => ({ ...s, [f.item_key]: e.target.value }))}
                placeholder="e.g. Re-sanitise surface and attach photo evidence"
                data-testid={`verify-ca-input-${f.item_key}`}
              />
            </div>
          ))}
          <div>
            <label className="text-xs opacity-70">General verifier notes (optional)</label>
            <textarea
              className="vb-input" rows={2} maxLength={500}
              value={notes} onChange={(e) => setNotes(e.target.value)}
              data-testid="verify-ca-notes"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="vb-btn ghost" onClick={onClose} data-testid="verify-ca-cancel">Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy || !allFilled} data-testid="verify-ca-submit">
              <ShieldCheck size={13} /> {busy ? "Verifying…" : "Verify & assign"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

// --------------------------- Rectify Item Modal (staff) ---------------------------
function RectifyItemModal({ check, item, template, onClose, onSaved }) {
  const itemLabel = (key) => {
    for (const sec of template?.sections || []) {
      const it = (sec.items || []).find((i) => i.key === key);
      if (it) return it.label;
    }
    return key;
  };
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isResubmit = item.state === "rejected";

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!text.trim()) { setErr("Please describe what you did to fix it."); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/checks/${check.id}/rectify`, {
        item_key: item.item_key,
        rectification_notes: text.trim(),
        photos,
      });
      onSaved?.();
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Rectification failed");
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="vb-modal-bg z-[1000]" onClick={onClose} data-testid="rectify-backdrop">
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }} data-testid="rectify-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="vb-heading text-xl m-0">
              {isResubmit ? "Resubmit rectification" : "Mark item as rectified"}
            </h2>
            <p className="text-xs opacity-60 m-0">
              {check.scope_target_name ? <>{check.scope_target_name} · </> : null}
              {item.label || itemLabel(item.item_key)}
            </p>
          </div>
          <button className="vb-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>
        {err && <div className="vb-pill err mb-3" data-testid="rectify-error">{err}</div>}

        <div className="vb-card p-3 mb-3" style={{ background: "var(--vb-bg-2)" }}>
          <div className="text-[11px] uppercase tracking-wider opacity-60">
            Supervisor's corrective action
          </div>
          <div className="text-sm mt-0.5">{item.corrective_action || "—"}</div>
          {item.corrective_action_set_by_name && (
            <div className="text-[11px] opacity-60 mt-1">
              Assigned by {item.corrective_action_set_by_name}
            </div>
          )}
          {(item.photos || []).length > 0 && (
            <PhotoThumbs photos={item.photos} label="Failure evidence" testidPrefix="rectify-fail-photo" />
          )}
          {isResubmit && item.rectification_rejection_notes && (
            <div className="mt-2 text-[12px]" style={{ color: "var(--vb-error)" }}>
              <strong>Previously rejected:</strong> {item.rectification_rejection_notes}
            </div>
          )}
        </div>

        {item.rectification_history?.length > 0 && (
          <div className="mb-3" data-testid="rectify-history-wrap">
            <div className="text-xs uppercase tracking-wider opacity-60 mb-1">
              Previous reworks · {item.rectification_history.length} attempt{item.rectification_history.length === 1 ? "" : "s"}
            </div>
            <ReworkHistory history={item.rectification_history} testidPrefix="rectify-rework" />
          </div>
        )}

        <form onSubmit={submit} className="space-y-3" data-testid="rectify-form">
          <div>
            <label className="text-xs opacity-70">What you did to fix it (required)</label>
            <textarea
              className="vb-input" rows={3} maxLength={500}
              value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Describe the action you took to resolve this item"
              data-testid="rectify-input"
              autoFocus
            />
            <PhotoEvidenceField
              photos={photos} onChange={setPhotos} kind="rectification"
              label="Add photo of rectified task"
              testid="rectify-photos"
              onUploadingChange={setUploading}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="vb-btn ghost" onClick={onClose} data-testid="rectify-cancel">Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy || uploading || !text.trim()} data-testid="rectify-submit">
              <Wrench size={13} /> {uploading ? "Uploading photo…" : busy ? "Submitting…" : (isResubmit ? "Resubmit" : "Submit rectification")}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

// --------------------------- Approve Rectification Modal (supervisor) ---------------------------
function ApproveRectificationModal({ check, item, template, onClose, onSaved }) {
  const itemLabel = (key) => {
    for (const sec of template?.sections || []) {
      const it = (sec.items || []).find((i) => i.key === key);
      if (it) return it.label;
    }
    return key;
  };
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(null);  // 'approve' | 'reject' | null
  const [err, setErr] = useState("");

  const act = async (decision) => {
    if (decision === "reject" && !notes.trim()) {
      setErr("Please add a reason for rejection.");
      return;
    }
    setBusy(decision); setErr("");
    try {
      await api.post(`/checks/${check.id}/approve-rectification`, {
        item_key: item.item_key,
        decision,
        notes: notes.trim(),
      });
      onSaved?.();
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Decision failed");
    } finally { setBusy(null); }
  };

  return createPortal(
    <div className="vb-modal-bg z-[1000]" onClick={onClose} data-testid="approve-backdrop">
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }} data-testid="approve-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="vb-heading text-xl m-0">Review rectification</h2>
            <p className="text-xs opacity-60 m-0">
              {check.scope_target_name ? <>{check.scope_target_name} · </> : null}
              {item.label || itemLabel(item.item_key)}
            </p>
          </div>
          <button className="vb-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>
        {err && <div className="vb-pill err mb-3" data-testid="approve-error">{err}</div>}

        <div className="space-y-2 mb-3">
          <div className="vb-card p-3" style={{ background: "var(--vb-bg-2)" }}>
            <div className="text-[11px] uppercase tracking-wider opacity-60">Corrective action assigned</div>
            <div className="text-sm mt-0.5">{item.corrective_action || "—"}</div>
            {(item.photos || []).length > 0 && (
              <PhotoThumbs photos={item.photos} label="Failure evidence" testidPrefix="approve-fail-photo" />
            )}
          </div>
          {item.rectification_history?.length ? (
            <div>
              <div className="text-xs uppercase tracking-wider opacity-60 mb-1">
                Rework history · {item.rectification_history.length} attempt{item.rectification_history.length === 1 ? "" : "s"}
              </div>
              <ReworkHistory history={item.rectification_history} testidPrefix="approve-rework" />
            </div>
          ) : (
            <div className="vb-card p-3" style={{ background: "var(--vb-bg-2)" }}>
              <div className="text-[11px] uppercase tracking-wider opacity-60">Staff's rectification</div>
              <div className="text-sm mt-0.5">{item.rectification_notes || "—"}</div>
              {item.rectified_by_name && (
                <div className="text-[11px] opacity-60 mt-1">
                  {item.rectified_by_name} · {new Date(item.rectified_at).toLocaleString()}
                </div>
              )}
              {(item.rectification_photos || []).length > 0 && (
                <PhotoThumbs photos={item.rectification_photos} label="Rectification evidence" testidPrefix="approve-rect-photo" />
              )}
            </div>
          )}
        </div>

        <div>
          <label className="text-xs opacity-70">Notes (required when rejecting)</label>
          <textarea
            className="vb-input" rows={2} maxLength={500}
            value={notes} onChange={(e) => setNotes(e.target.value)}
            data-testid="approve-notes"
          />
        </div>

        <div className="flex justify-end gap-2 mt-3 flex-wrap">
          <button type="button" className="vb-btn ghost" onClick={onClose} data-testid="approve-cancel">Cancel</button>
          <button
            type="button" className="vb-btn"
            style={{ background: "var(--vb-error)", borderColor: "var(--vb-error)" }}
            onClick={() => act("reject")} disabled={!!busy}
            data-testid="approve-reject"
          >
            <ThumbsDown size={13} /> {busy === "reject" ? "Rejecting…" : "Reject — send back"}
          </button>
          <button
            type="button" className="vb-btn"
            onClick={() => act("approve")} disabled={!!busy}
            data-testid="approve-confirm"
          >
            <ThumbsUp size={13} /> {busy === "approve" ? "Approving…" : "Approve — close item"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// --------------------------- Rectification Queue Panel ---------------------------
/**
 * Combined queue panel that surfaces records needing per-item rectification
 * (staff side) or approval (supervisor side). Records are pre-decorated by
 * the backend with `rectification_phase` and per-item `state`.
 */
export function RectificationPanel({ records, template, onChanged, title, mode = "rectify" }) {
  if (!records || records.length === 0) return null;
  const headerIcon = mode === "rectify"
    ? <Wrench size={18} style={{ color: "var(--vb-text-2)" }} />
    : <RotateCcw size={18} style={{ color: "var(--vb-text-2)" }} />;

  return (
    <section data-testid={`rectification-section-${mode}`}>
      <div className="flex items-center gap-2 mb-2">
        {headerIcon}
        <h2 className="vb-heading text-lg m-0" style={{ color: "var(--vb-text-2)" }}>{title}</h2>
        <span className="vb-pill warn text-[10px]">{records.length}</span>
      </div>
      <div className="vb-card p-4 space-y-2">
        {records.map((c) => (
          <CheckRow
            key={c.id} check={c} template={template}
            canVerify={mode === "approve"}
            onVerified={onChanged}
          />
        ))}
      </div>
    </section>
  );
}

